import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, LinearProgress, Divider, Paper, Table,
  TableHead, TableRow, TableCell, Checkbox, FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HotelIcon from '@mui/icons-material/Hotel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import StarIcon from '@mui/icons-material/Star';
import {
  MY_ACCOMMODATION_BLOCKS_QUERY, MY_CEREMONIES_QUERY,
  CREATE_ACCOMMODATION_BLOCK_MUTATION, UPDATE_ACCOMMODATION_BLOCK_MUTATION, DELETE_ACCOMMODATION_BLOCK_MUTATION,
  ASSIGN_GUEST_ROOM_MUTATION, UPDATE_GUEST_ACCOMMODATION_MUTATION, DELETE_GUEST_ACCOMMODATION_MUTATION,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLOR = { active:'success', full:'warning', expired:'default', cancelled:'error' };
const ROOM_STATUS_COLOR = { reserved:'info', checked_in:'success', checked_out:'default', cancelled:'error' };
const fmt = n => n!=null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const EMPTY_BLOCK = { hotelName:'', address:'', city:'', starRating:'', contactName:'', contactPhone:'', contactEmail:'', roomsBlocked:'', pricePerNight:'', checkInDate:'', checkOutDate:'', shuttleAvailable:false, shuttleSchedule:'', specialRateCode:'', bookingDeadline:'', notes:'', ceremonyId:'' };
const EMPTY_ROOM  = { guestName:'', roomType:'', roomNumber:'', checkIn:'', checkOut:'', numGuests:1, dietaryPref:'', paidBy:'family', specialRequests:'', notes:'' };

export default function AccommodationPage() {
  const [error, setError]       = useState('');
  const [blockDialog, setBlockDialog] = useState(false);
  const [editBlock, setEditBlock]     = useState(null);
  const [blockForm, setBlockForm]     = useState(EMPTY_BLOCK);
  const [activeBlockId, setActiveBlockId] = useState('');
  const [roomDialog, setRoomDialog]   = useState(false);
  const [editRoom, setEditRoom]       = useState(null);
  const [roomForm, setRoomForm]       = useState(EMPTY_ROOM);
  const [confirmDel, setConfirmDel]   = useState(null);

  const { data, loading, refetch } = useQuery(MY_ACCOMMODATION_BLOCKS_QUERY, { fetchPolicy:'cache-and-network' });
  const { data: cerData }           = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy:'cache-and-network' });

  const blocks    = data?.myAccommodationBlocks || [];
  const ceremonies = cerData?.myCeremonies || [];
  const activeBlock = blocks.find(b=>b.id===activeBlockId);

  const [createBlock] = useMutation(CREATE_ACCOMMODATION_BLOCK_MUTATION, { onCompleted:()=>{ setBlockDialog(false); setBlockForm(EMPTY_BLOCK); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [updateBlock] = useMutation(UPDATE_ACCOMMODATION_BLOCK_MUTATION, { onCompleted:()=>{ setBlockDialog(false); setEditBlock(null); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [deleteBlock] = useMutation(DELETE_ACCOMMODATION_BLOCK_MUTATION, { onCompleted:()=>{ setConfirmDel(null); if(activeBlockId===confirmDel) setActiveBlockId(''); refetch(); } });
  const [assignRoom]  = useMutation(ASSIGN_GUEST_ROOM_MUTATION, { onCompleted:()=>{ setRoomDialog(false); setRoomForm(EMPTY_ROOM); refetch(); }, onError:e=>setError(e.message) });
  const [updateRoom]  = useMutation(UPDATE_GUEST_ACCOMMODATION_MUTATION, { onCompleted:()=>{ setRoomDialog(false); setEditRoom(null); refetch(); }, onError:e=>setError(e.message) });
  const [deleteRoom]  = useMutation(DELETE_GUEST_ACCOMMODATION_MUTATION, { onCompleted:()=>refetch() });

  const openEditBlock = (b) => {
    setEditBlock(b);
    setBlockForm({ hotelName:b.hotelName||'', address:b.address||'', city:b.city||'', starRating:b.starRating||'', contactName:b.contactName||'', contactPhone:b.contactPhone||'', contactEmail:b.contactEmail||'', roomsBlocked:b.roomsBlocked||'', pricePerNight:b.pricePerNight||'', checkInDate:b.checkInDate||'', checkOutDate:b.checkOutDate||'', shuttleAvailable:b.shuttleAvailable||false, shuttleSchedule:b.shuttleSchedule||'', specialRateCode:b.specialRateCode||'', bookingDeadline:b.bookingDeadline||'', notes:b.notes||'', ceremonyId:b.ceremonyId||'' });
    setBlockDialog(true);
  };
  const openEditRoom = (r) => {
    setEditRoom(r);
    setRoomForm({ guestName:r.guestName||'', roomType:r.roomType||'', roomNumber:r.roomNumber||'', checkIn:r.checkIn||'', checkOut:r.checkOut||'', numGuests:r.numGuests||1, dietaryPref:r.dietaryPref||'', paidBy:r.paidBy||'family', specialRequests:r.specialRequests||'', notes:r.notes||'' });
    setRoomDialog(true);
  };
  const handleSaveBlock = () => {
    const vars = { ...blockForm, roomsBlocked:parseInt(blockForm.roomsBlocked)||0, starRating:blockForm.starRating?parseInt(blockForm.starRating):undefined, pricePerNight:blockForm.pricePerNight?parseFloat(blockForm.pricePerNight):undefined, ceremonyId: blockForm.ceremonyId || null };
    editBlock ? updateBlock({ variables:{ id:editBlock.id, ...vars } }) : createBlock({ variables:vars });
  };
  const handleSaveRoom = () => {
    const vars = { ...roomForm, numGuests:parseInt(roomForm.numGuests)||1 };
    editRoom ? updateRoom({ variables:{ id:editRoom.id, ...vars } }) : assignRoom({ variables:{ blockId:activeBlockId, ...vars } });
  };

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Guest Accommodation</Typography>
          <Typography variant="body2" color="text.secondary">Manage hotel blocks & room assignments for out-of-town guests</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>{ setEditBlock(null); setBlockForm(EMPTY_BLOCK); setBlockDialog(true); }}>Add Hotel Block</Button>
      </Stack>
      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{mb:2}}/>}

      <Grid container spacing={3}>
        {/* Hotel cards */}
        <Grid item xs={12} md={4}>
          {blocks.length===0 && !loading && <Typography color="text.secondary" sx={{textAlign:'center',mt:4}}>No hotel blocks yet.</Typography>}
          <Paged items={blocks}>{rows => rows.map(b=>(
            <Card key={b.id} variant="outlined" onClick={()=>setActiveBlockId(b.id)}
              sx={{ mb:1.5, cursor:'pointer', borderColor:activeBlockId===b.id?'primary.main':'divider' }}>
              <CardContent sx={{py:1.5,'&:last-child':{pb:1.5}}}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={600}>{b.hotelName}</Typography>
                  <Chip size="small" label={b.status} color={STATUS_COLOR[b.status]||'default'}/>
                </Stack>
                <Typography variant="caption" color="text.secondary">{b.city}</Typography>
                <Stack direction="row" spacing={1} mt={0.5}>
                  {Array.from({length:b.starRating||0}).map((_,i)=><StarIcon key={i} sx={{fontSize:12,color:'#f9a825'}}/>)}
                </Stack>
                <Stack direction="row" justifyContent="space-between" mt={1}>
                  <Typography variant="caption">{b.roomsAllocated||0}/{b.roomsBlocked} rooms allocated</Typography>
                  <Typography variant="caption" fontWeight={600}>{fmt(b.pricePerNight)}/night</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} mt={1}>
                  <Tooltip title="Edit"><IconButton size="small" onClick={e=>{e.stopPropagation();openEditBlock(b);}}><EditIcon fontSize="small"/></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={e=>{e.stopPropagation();setConfirmDel(b.id);}}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ))}</Paged>
        </Grid>

        {/* Right detail */}
        <Grid item xs={12} md={8}>
          {!activeBlock ? (
            <Box sx={{textAlign:'center',mt:8,color:'text.secondary'}}>
              <HotelIcon sx={{fontSize:64,opacity:.2}}/>
              <Typography mt={1}>Select a hotel block to manage room assignments</Typography>
            </Box>
          ) : (
            <Paper variant="outlined" sx={{p:3,borderRadius:2}}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{activeBlock.hotelName}</Typography>
                  <Typography variant="body2" color="text.secondary">{activeBlock.address}</Typography>
                  {activeBlock.shuttleAvailable && (
                    <Chip size="small" icon={<DirectionsBusIcon/>} label="Shuttle Available" color="info" sx={{mt:0.5}}/>
                  )}
                </Box>
                <Button variant="outlined" size="small" startIcon={<PersonAddIcon/>}
                  onClick={()=>{ setEditRoom(null); setRoomForm(EMPTY_ROOM); setRoomDialog(true); }}
                  disabled={(activeBlock.roomsAllocated||0)>=(activeBlock.roomsBlocked||0)}>
                  Assign Room
                </Button>
              </Stack>

              {/* Hotel info */}
              <Grid container spacing={1.5} mb={2}>
                {[
                  ['Check-In', activeBlock.checkInDate||'—'],
                  ['Check-Out', activeBlock.checkOutDate||'—'],
                  ['Rate Code', activeBlock.specialRateCode||'—'],
                  ['Contact', activeBlock.contactPhone||'—'],
                ].map(([k,v])=>(
                  <Grid item xs={6} sm={3} key={k}>
                    <Box sx={{bgcolor:'grey.50',borderRadius:1,p:1.5}}>
                      <Typography variant="caption" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={600}>{v}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} mb={1}>Room Assignments ({activeBlock.roomsAllocated||0} / {activeBlock.roomsBlocked})</Typography>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Guest</TableCell><TableCell>Room</TableCell><TableCell>Check-In</TableCell><TableCell>Check-Out</TableCell><TableCell>Status</TableCell><TableCell/>
                </TableRow></TableHead>
                <TableBody>
                  {(activeBlock.roomAllocations||[]).map(r=>(
                    <TableRow key={r.id}>
                      <TableCell>{r.guestName}</TableCell>
                      <TableCell>{r.roomType||'—'} {r.roomNumber?`(${r.roomNumber})`:''}</TableCell>
                      <TableCell>{r.checkIn||'—'}</TableCell>
                      <TableCell>{r.checkOut||'—'}</TableCell>
                      <TableCell><Chip size="small" label={r.status} color={ROOM_STATUS_COLOR[r.status]||'default'}/></TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={()=>openEditRoom(r)}><EditIcon fontSize="small"/></IconButton>
                        <IconButton size="small" color="error" onClick={()=>deleteRoom({variables:{id:r.id}})}><DeleteIcon fontSize="small"/></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(activeBlock.roomAllocations||[]).length===0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" sx={{textAlign:'center', py:2}}>No rooms assigned yet.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Hotel Block Dialog */}
      <Dialog open={blockDialog} onClose={()=>setBlockDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editBlock?'Edit Hotel Block':'Add Hotel Block'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="Hotel Name *" value={blockForm.hotelName} onChange={e=>setBlockForm(f=>({...f,hotelName:e.target.value}))} fullWidth/>
          <Grid container spacing={1.5}>
            <Grid item xs={8}><TextField label="Address" value={blockForm.address} onChange={e=>setBlockForm(f=>({...f,address:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={4}><TextField label="City" value={blockForm.city} onChange={e=>setBlockForm(f=>({...f,city:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={4}><TextField select label="Star Rating" value={blockForm.starRating} onChange={e=>setBlockForm(f=>({...f,starRating:e.target.value}))} fullWidth>{[1,2,3,4,5].map(n=><MenuItem key={n} value={n}>{n} star</MenuItem>)}</TextField></Grid>
            <Grid item xs={4}><TextField label="Rooms Blocked *" type="number" value={blockForm.roomsBlocked} onChange={e=>setBlockForm(f=>({...f,roomsBlocked:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={4}><TextField label="Price/Night (₹)" type="number" value={blockForm.pricePerNight} onChange={e=>setBlockForm(f=>({...f,pricePerNight:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Check-In Date" type="date" value={blockForm.checkInDate} onChange={e=>setBlockForm(f=>({...f,checkInDate:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={6}><TextField label="Check-Out Date" type="date" value={blockForm.checkOutDate} onChange={e=>setBlockForm(f=>({...f,checkOutDate:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Contact Name" value={blockForm.contactName} onChange={e=>setBlockForm(f=>({...f,contactName:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Contact Phone" value={blockForm.contactPhone} onChange={e=>setBlockForm(f=>({...f,contactPhone:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <TextField label="Rate Code" value={blockForm.specialRateCode} onChange={e=>setBlockForm(f=>({...f,specialRateCode:e.target.value}))} fullWidth/>
          <FormControlLabel control={<Checkbox checked={!!blockForm.shuttleAvailable} onChange={e=>setBlockForm(f=>({...f,shuttleAvailable:e.target.checked}))}/>} label="Shuttle Available"/>
          {blockForm.shuttleAvailable && <TextField label="Shuttle Schedule" value={blockForm.shuttleSchedule} onChange={e=>setBlockForm(f=>({...f,shuttleSchedule:e.target.value}))} fullWidth/>}
          <TextField label="Notes" value={blockForm.notes} onChange={e=>setBlockForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setBlockDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBlock} disabled={!blockForm.hotelName||!blockForm.roomsBlocked}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Room Assignment Dialog */}
      <Dialog open={roomDialog} onClose={()=>setRoomDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editRoom?'Edit Room Assignment':'Assign Room'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="Guest Name *" value={roomForm.guestName} onChange={e=>setRoomForm(f=>({...f,guestName:e.target.value}))} fullWidth/>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Room Type" value={roomForm.roomType} onChange={e=>setRoomForm(f=>({...f,roomType:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Room No." value={roomForm.roomNumber} onChange={e=>setRoomForm(f=>({...f,roomNumber:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Check-In" type="date" value={roomForm.checkIn} onChange={e=>setRoomForm(f=>({...f,checkIn:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={6}><TextField label="Check-Out" type="date" value={roomForm.checkOut} onChange={e=>setRoomForm(f=>({...f,checkOut:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="# Guests" type="number" value={roomForm.numGuests} onChange={e=>setRoomForm(f=>({...f,numGuests:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField select label="Paid By" value={roomForm.paidBy} onChange={e=>setRoomForm(f=>({...f,paidBy:e.target.value}))} fullWidth>
              {['family','self','organiser'].map(p=><MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField></Grid>
          </Grid>
          <TextField label="Dietary Preference" value={roomForm.dietaryPref} onChange={e=>setRoomForm(f=>({...f,dietaryPref:e.target.value}))} fullWidth/>
          <TextField label="Special Requests" value={roomForm.specialRequests} onChange={e=>setRoomForm(f=>({...f,specialRequests:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setRoomDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRoom} disabled={!roomForm.guestName}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Delete Hotel Block?</DialogTitle>
        <DialogContent><Typography>This will remove the block and all its room assignments.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteBlock({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
